import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import api from '../../services/api';
import { User, ApiResponse } from '../../types';

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
}

const initialState: AuthState = {
  user: null,
  token: localStorage.getItem('authToken'),
  isAuthenticated: !!localStorage.getItem('authToken'),
  loading: false,
  error: null,
};

export const login = createAsyncThunk(
  'auth/login',
  async ({ email, password }: { email: string; password: string }, { rejectWithValue }) => {
    try {
      // Use real backend API for authentication
      const response = await api.post<ApiResponse<{ accessToken: string; refreshToken: string; tokenType: string; expiresIn: string; scope: string }>>(
        '/auth/token',
        { 
          clientId: email.replace('@gcpayment.com', ''), // Convert email to clientId
          clientSecret: password 
        }
      );
      
      const { accessToken } = response.data.data;
      
      // Create user object - since backend doesn't return client info in this endpoint,
      // we'll create a default admin user based on the clientId
      const user: User = {
        id: '1',
        email: email,
        name: 'GC Payment Admin',
        role: 'admin' as const,
        createdAt: new Date().toISOString(),
      };
      
      return { user, token: accessToken };
    } catch (error: any) {
      // SECURITY: Mock authentication ONLY for development environment
      // Support both 'admin@gcpayment.com' and 'admin' for convenience
      const isValidAdmin = (
        (email === 'admin@gcpayment.com' || email === 'admin') && 
        password === 'admin123'
      );
      
      if (process.env.NODE_ENV === 'development' && isValidAdmin) {
        
        console.warn('🚨 SECURITY WARNING: Using mock authentication in development mode');
        
        const mockUser: User = {
          id: '1',
          email: 'admin@gcpayment.com',
          name: 'GC Payment Admin (DEV)',
          role: 'admin' as const,
          createdAt: new Date().toISOString(),
        };
        const mockToken = 'mock-jwt-token-dev-' + Date.now();
        return { user: mockUser, token: mockToken };
      }
      
      // Production: Always reject invalid credentials
      return rejectWithValue(error.response?.data?.message || error.message || 'ログインに失敗しました');
    }
  }
);

export const logout = createAsyncThunk('auth/logout', async () => {
  // Backend doesn't have a logout endpoint since JWT is stateless
  // Just remove token from localStorage
  try {
    localStorage.removeItem('authToken');
  } catch (error) {
    // Handle localStorage errors silently
    console.warn('Failed to remove auth token from localStorage:', error);
  }
});

export const getCurrentUser = createAsyncThunk(
  'auth/getCurrentUser',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('No token found');
      }
      
      // For JWT-based auth, we can decode token or create mock user
      // Since backend uses client-based auth, return mock user for now
      if (token.startsWith('mock-jwt-token')) {
        const mockUser: User = {
          id: '1',
          email: 'admin@gcpayment.com',
          name: 'GC Payment Admin',
          role: 'admin' as const,
          createdAt: new Date().toISOString(),
        };
        return mockUser;
      }
      
      // In a real implementation, you would decode JWT or call an endpoint
      // For now, return default admin user
      const user: User = {
        id: '1',
        email: 'admin@gcpayment.com', 
        name: 'GC Payment Admin',
        role: 'admin' as const,
        createdAt: new Date().toISOString(),
      };
      
      return user;
    } catch (error: any) {
      return rejectWithValue(error.message || 'ユーザー情報の取得に失敗しました');
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearAuth: (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      try {
        localStorage.removeItem('authToken');
      } catch (error) {
        console.warn('Failed to remove auth token from localStorage:', error);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action: PayloadAction<{ user: User; token: string }>) => {
        state.loading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.isAuthenticated = true;
        state.error = null; // Clear any previous errors
        try {
          localStorage.setItem('authToken', action.payload.token);
          console.log('🔐 Authentication successful, token saved');
        } catch (error) {
          console.warn('Failed to save auth token to localStorage:', error);
        }
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || action.error?.message || 'ログインに失敗しました';
      })
      // Logout
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
        // localStorage removal is handled in the thunk
      })
      // Get current user
      .addCase(getCurrentUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(getCurrentUser.fulfilled, (state, action: PayloadAction<User>) => {
        state.loading = false;
        state.user = action.payload;
        state.isAuthenticated = true;
      })
      .addCase(getCurrentUser.rejected, (state, action) => {
        state.loading = false;
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
        try {
          localStorage.removeItem('authToken');
        } catch (error) {
          console.warn('Failed to remove auth token from localStorage:', error);
        }
      });
  },
});

export const { clearError, clearAuth } = authSlice.actions;
export default authSlice.reducer;